<template>
  <div>
    <VLayout>
      <VMain>
        <router-view></router-view>
        <v-snackbar v-model="globalStore.error" timeout="2500">
          {{ globalStore.errorMessage }}

          <template v-slot:actions>
            <v-btn
              color="pink"
              variant="text"
              @click="globalStore.error = false"
            >
              {{ $t("close") }}
            </v-btn>
          </template>
        </v-snackbar>
        <v-snackbar v-model="globalStore.notice" timeout="5000">
          {{ globalStore.noticeMessage }}

          <template v-slot:actions>
            <v-btn
              variant="text"
              @click="globalStore.notice = false"
            >
              {{ $t("close") }}
            </v-btn>
          </template>
        </v-snackbar>
      </VMain>
    </VLayout>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Routes } from "./types/enums/routes.enum";
import { useGlobalStore } from "./stores/global.store";
import { registerOwnerServiceWorker } from "./services/pwa.service";
import { autoRequestPushPermission } from "./services/firebase.service";

const route = useRoute();
const router = useRouter();

const globalStore = useGlobalStore();

// Registered unconditionally on boot (not gated to being on /ad/ already) so
// that an installed PWA has a controlling service worker from its very first
// cold launch, even when that first launch happens while logged out (its
// start_url is /ad/, but router.plugin.ts's beforeEach guard may bounce that
// to the login screen before this component's route reflects /ad/). The
// service worker's own registration scope ("/ad/", set in pwa.service.ts)
// already keeps guest booking pages uncontrolled - gating the *call* on the
// current route was redundant for that purpose and only made the SW miss
// its own start_url in this scenario.
let serviceWorkerRegistered = false;
const registerServiceWorkerOnce = () => {
  if (serviceWorkerRegistered) return;
  serviceWorkerRegistered = true;
  registerOwnerServiceWorker();
};

// autoRequestPushPermission() (not just a granted-permission check) needs to
// run here too, not only from Login.vue's submit handler - a returning owner
// who's already authenticated (persisted session) never touches the login
// form, e.g. reopening an installed PWA, so that was the only place the
// browser's permission prompt ever got triggered from. Still scoped to /ad/
// so a logged-out visitor is never prompted for push permission.
let pushPermissionRequested = false;
const requestPushPermissionIfNeeded = async (path: string) => {
  if (pushPermissionRequested || !path.startsWith(Routes.Admin)) return;
  pushPermissionRequested = true;
  await autoRequestPushPermission();
};

watch(() => route.path, (path) => requestPushPermissionIfNeeded(path));

onMounted(async () => {
  // On a hard page load, `route.path` is still START_LOCATION's "/" until the
  // router's initial navigation resolves — awaiting isReady() first means the
  // checks below see the real, auth-guard-resolved path (e.g. redirected to
  // Routes.Main when logged out) instead of racing it.
  await router.isReady();

  const queryParams = new URL(location.href).searchParams;
  if (queryParams.get("confirmation") == "1") {
    alert(
      "Ваш аккаунт успішно підтверджено! Тепер ви можете увійти в систему."
    );
  }

  registerServiceWorkerOnce();
  requestPushPermissionIfNeeded(route.path);
});
</script>

<style lang="scss">
* {
  font-family: "Montserrat", sans-serif;
}

body {
  min-height: 100vh;
  background-repeat: no-repeat;
  background: linear-gradient(
    180deg,
    #fddbdb 0%,
    #d6bede 47.4%,
    #cebee2 80.21%,
    #d5cce7 100%
  );
}
</style>
